import { useEffect, useState } from "react";
import {
  Button,
  Grid,
  Typography,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@material-ui/core";
import Api from "lib/api/httpRequestHelper";
import { IsMobile } from "utils/is-mobile";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import InputText from "components/_refactor/text/input";
import ChooseInvoiceDialog from "../components/choose-invoice-dialog";

function mapApiNotaToInvoiceObj(nota) {
  const idPedido = nota.IdPedido != null ? nota.IdPedido : null;
  const lista = nota.ListaProdutos || [];

  const listaProdutosExport = lista.map((item) => ({
    Codigo: item.CodigoProduto,
    Descricao: item.DescricaoProduto,
    QtdFaturada: item.QtdFaturada,
    QuantidadeDevolvida: item.QuantidadeDevolvida ?? 0,
    ValorUnitario: item.ValorUnitario ?? 0,
    NF: item.Numero,
    Serie: item.Serie,
  }));

  return {
    NumeroNf: nota.NumeroNf ?? null,
    ValorTotal: nota.ValorLiquidoNf ?? null,
    SerieNf: nota.SerieNf || null,
    DataEmissaoNf: nota.DataEmissaoNf ?? null,
    ChaveNf: nota.ChaveNf || null,
    IdPedido: idPedido,
    items: listaProdutosExport.map((item) => {
      const qf = Number(item.QtdFaturada) || 0;
      const vu = Number(item.ValorUnitario) || 0;
      const qd = Number(item.QuantidadeDevolvida) || 0;
      return {
        selected: false,
        idTableProduct: idPedido,
        product: item,
        name: item.Descricao || null,
        idProduto: item.Codigo || null,
        nameMotivo: undefined,
        nameDefeito: undefined,
        // Não pré-preencher com a quantidade disponível (qf - qd) — isso fazia o campo de
        // devolução vir preenchido mesmo em produtos ainda não selecionados. O próprio dialog já
        // seta essa quantidade quando o usuário marca o checkbox (handleChangeSelected); aqui só
        // deve vir preenchido se já havia uma seleção anterior para o mesmo produto/nota
        // (getInitialProducts, em choose-invoice-dialog.js, restaura isso via selectedProductsList).
        quantity: null,
        motivo: undefined,
        defeito: undefined,
        IdPedido: idPedido,
        QtdFaturada: item.QtdFaturada || null,
        QuantidadeDevolvida: qd,
        ValorUnitario: item.ValorUnitario || null,
        ValorTotal: (qf * vu).toFixed(2),
        NumeroNota: item.NF ?? nota.NumeroNf,
        Serie: item.Serie ?? nota.SerieNf,
      };
    }),
  };
}

export default function InvoiceChoose({
  fieldsData,
  loadingFields,
  isLoading,
  setIsLoading,
  addedProductsList,
  setAddedProductsList,
  clearFilters,
  motivoRequerEvidencia,
}) {
  const [invoice, setInvoice] = useState("");
  const [invoiceSelected, setInvoiceSelected] = useState(null);
  const [isInvoiceValid, setIsInvoiceValid] = useState();

  const [openDialog, setOpenDialog] = useState(false);
  const [openInvoicePicker, setOpenInvoicePicker] = useState(false);
  const [invoiceOptions, setInvoiceOptions] = useState([]);
  const [searchingInvoice, setSearchingInvoice] = useState("");

  const snackbar = useSnackbar();
  const isMobile = IsMobile();

  const openInvoiceWithNota = (nota) => {
    const produtos = nota.ListaProdutos || [];

    if (produtos.length === 0) {
      setIsInvoiceValid(false);
      snackbar.showMessage("Nota fiscal sem produtos disponíveis", {
        severity: "error",
      });
      setSearchingInvoice("");
      return;
    }

    const invoiceObj = mapApiNotaToInvoiceObj(nota);
    const invoiceNumberToRestore =
      searchingInvoice || String(nota.NumeroNf ?? "") || invoice;

    setInvoiceSelected(invoiceObj);
    setIsInvoiceValid(true);
    setOpenDialog(true);
    setOpenInvoicePicker(false);
    setInvoiceOptions([]);

    if (!invoice && invoiceNumberToRestore) {
      setInvoice(String(invoiceNumberToRestore));
    }

    setSearchingInvoice("");
  };

  const hasStructureFilter =
    fieldsData?.idGrupoEconomico ||
    fieldsData?.idPessoa ||
    (fieldsData?.cnpj || "").replace(/\D/g, "").length === 14;

  const handleSearchInvoice = async () => {
    const invoiceValue = invoice.trim();
    const cnpjDigits = (fieldsData?.cnpj || "").replace(/\D/g, "");

    if (!invoiceValue) {
      return;
    }

    if (!hasStructureFilter) {
      snackbar.showMessage(
        "Informe o grupo econômico, a loja ou o CNPJ para pesquisar a nota.",
        { severity: "warning" },
      );
      return;
    }

    if (cnpjDigits.length > 0 && cnpjDigits.length !== 14) {
      snackbar.showMessage("Informe um CNPJ válido com 14 dígitos.", {
        severity: "warning",
      });
      return;
    }

    setIsInvoiceValid(undefined);
    setSearchingInvoice(invoiceValue);
    setIsLoading(true);

    try {
      const qs = new URLSearchParams({
        simples: "true",
        numeroNf: invoiceValue,
        situacao: "todos",
        ordenacao: "4",
      });

      if (fieldsData?.idGrupoEconomico) {
        qs.set("idGrupoEconomico", String(fieldsData.idGrupoEconomico));
      }

      if (fieldsData?.idPessoa) {
        qs.set("idLoja", String(fieldsData.idPessoa));
      }

      if (cnpjDigits.length === 14) {
        qs.set("cnpj", cnpjDigits);
      }

      const response = await Api.get(
        "list-reception-invoices",
        "invoiceDigitalApi",
        `?${qs.toString()}`,
        true,
      );

      const validation =
        response?.ValidationMessage || response?.validationMessage;
      if (validation) {
        snackbar.showMessage(String(validation), { severity: "error" });
        setIsInvoiceValid(false);
        setSearchingInvoice("");
        return;
      }

      if (response?.Success === false) {
        const msg =
          response?.Message ||
          response?.message ||
          "Não foi possível consultar a nota fiscal.";
        snackbar.showMessage(String(msg), { severity: "error" });
        setIsInvoiceValid(false);
        setSearchingInvoice("");
        return;
      }

      const listaNotas = response?.Result?.ListaNotas;
      if (!listaNotas || listaNotas.length === 0) {
        setIsInvoiceValid(false);
        snackbar.showMessage(
          "Nenhuma nota fiscal encontrada para os filtros informados.",
          { severity: "info" },
        );
        setSearchingInvoice("");
        return;
      }

      const pedidosComProdutos = listaNotas.filter(
        (nota) => (nota.ListaProdutos || []).length > 0,
      );

      if (pedidosComProdutos.length === 0) {
        setIsInvoiceValid(false);
        snackbar.showMessage("Pedido sem produtos disponíveis", {
          severity: "error",
        });
        setSearchingInvoice("");
        return;
      }

      const requerSelecaoPedido =
        response?.Result?.RequerSelecaoPedido === true ||
        pedidosComProdutos.length > 1;

      if (!requerSelecaoPedido) {
        openInvoiceWithNota(pedidosComProdutos[0]);
        return;
      }

      setInvoiceOptions(pedidosComProdutos);
      setOpenInvoicePicker(true);
      setSearchingInvoice("");
    } catch (error) {
      const msg =
        typeof error === "string"
          ? error
          : error?.message || "Erro ao pesquisar a nota fiscal.";
      snackbar.showMessage(msg, { severity: "error" });
      setIsInvoiceValid(false);
      setSearchingInvoice("");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (clearFilters === true && !openDialog && !openInvoicePicker) {
      setInvoice("");
      setInvoiceSelected(null);
      setIsInvoiceValid(undefined);
      setOpenDialog(false);
      setOpenInvoicePicker(false);
      setInvoiceOptions([]);
      setSearchingInvoice("");
    }
  }, [clearFilters, openDialog, openInvoicePicker]);

  useEffect(() => {
    if (searchingInvoice && !invoice && !isLoading) {
      setInvoice(searchingInvoice);
    }
  }, [searchingInvoice, invoice, isLoading]);

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <InputText
            label="Nota Fiscal"
            disabled={loadingFields.length > 0}
            value={invoice}
            onChange={(e) => {
              const value = e.target.value
                .toString()
                .replace(/\D/g, "")
                .slice(0, 10);
              setInvoice(value);
              setIsInvoiceValid(undefined);
            }}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Button
            style={{
              width: isMobile ? "100%" : "17vw",
              marginBottom: isMobile ? "0.75rem" : "0px",
              marginTop: isMobile ? "0.75rem" : "20px",
              height: "38px",
              alignSelf: "center",
              padding: "0",
            }}
            variant="contained"
            onClick={handleSearchInvoice}
            disabled={
              !invoice ||
              !hasStructureFilter ||
              loadingFields.length > 0 ||
              isLoading
            }
          >
            {isLoading ? "Pesquisando..." : "Pesquisar"}
          </Button>
        </Grid>

        {isInvoiceValid === false && (
          <Typography
            variant="h5"
            component="h5"
            style={{ color: "grey", marginTop: "1rem" }}
          >
            <i
              className="fa fa-exclamation-circle"
              aria-hidden="true"
              style={{
                fontSize: "1rem",
                marginRight: "0.5rem",
              }}
            />{" "}
            Nota informado não encontrada.
          </Typography>
        )}
      </Grid>

      <Divider variant="fullWidth" style={{ margin: "20px 0px" }} />

      {openInvoicePicker && invoiceOptions.length > 0 && (
        <Dialog
          open
          onClose={() => {
            setOpenInvoicePicker(false);
            setInvoiceOptions([]);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Selecione o pedido</DialogTitle>
          <DialogContent>
            <Typography variant="body2" style={{ marginBottom: "1rem" }}>
              Foram encontrados múltiplos pedidos vinculados a esta nota fiscal.
              Escolha o pedido desejado.
            </Typography>
            {invoiceOptions.map((nota) => (
              <Button
                key={`${nota.NumeroNf}-${nota.SerieNf}-${nota.IdPedido}`}
                variant="outlined"
                fullWidth
                style={{ marginBottom: "0.5rem", justifyContent: "flex-start" }}
                onClick={() => openInvoiceWithNota(nota)}
              >
                Pedido {nota.IdPedido}
                {nota.SerieNf ? ` · Série ${nota.SerieNf}` : ""}
                {nota.Cnpj ? ` · CNPJ ${nota.Cnpj}` : ""}
              </Button>
            ))}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setOpenInvoicePicker(false);
                setInvoiceOptions([]);
              }}
            >
              Cancelar
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {openDialog && invoiceSelected && (
        <ChooseInvoiceDialog
          onClose={() => {
            setOpenDialog(false);
            setInvoice("");
            setInvoiceSelected(null);
          }}
          invoice={invoiceSelected}
          selectedProductsList={addedProductsList}
          setSelectedProductsList={setAddedProductsList}
          motivoRequerEvidencia={motivoRequerEvidencia}
        />
      )}
    </>
  );
}
